<template>
  <section v-if="!isPageLoading" class="course">
    <article
      v-if="course"
      class="course__background"
      :style="
        `background-image: url(${$getCourseBackground(
          course,
          'large'
        )}), linear-gradient(${gradient});`
      "
    >
      <div class="course__overview-wrapper">
        <CourseOverview :course="course"></CourseOverview>
      </div>
      <div class="course__details-wrapper">
        <CourseDetails
          :course="course"
          :isAuthenticated="isAuthenticated"
          :isCordova="isCordova"
        ></CourseDetails>
      </div>
    </article>
    <div v-if="course" class="course__content-wrapper">
      <div class="course__progress-wrapper">
        <CourseProgress
          v-if="isUser"
          :currentProgress="progress"
          :progressPercentage="progressPercentage"
          @resume="resumeCourse"
        ></CourseProgress>
      </div>
      <Group
        v-for="group in course.groups"
        :key="`course-group-${group.id}`"
        :group="group"
        :courseId="course.id"
        :videos="course.videos"
        :isUser="isUser"
        :userId="userId"
        :progress="progress"
      ></Group>
      <UserCourseFeedback
        v-if="isUser"
        :userId="userId"
        :courseId="course.id"
        :isComplete="progressPercentage === 100"
        :hasRated="currentUserFeedback || false"
      ></UserCourseFeedback>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import CourseOverview from '@/components/course/CourseOverview.vue'
import CourseDetails from '@/components/course/CourseDetails.vue'
import Group from '@/components/course/content/Group.vue'
import CourseProgress from '@/components/course/CourseProgress.vue'
import UserCourseFeedback from '@/components/user/course-feedback/UserCourseFeedback.vue'

export default {
  name: 'Course',
  components: {
    CourseOverview,
    CourseDetails,
    CourseProgress,
    Group,
    UserCourseFeedback,
  },
  computed: {
    ...mapGetters({
      isPageLoading: 'app/isPageLoading',
      isAuthenticated: 'auth/isAuthenticated',
      course: 'courses/courseWithTopicLessons',
      userId: 'auth/userId',
      progress: 'user/progress',
      isCordova: 'app/isCordova',
    }),
    progressPercentage() {
      return this.isAuthenticated &&
        this.progress &&
        this.progress.watched_videos &&
        this.progress.watched_videos > 0
        ? Math.floor(
            (this.progress.watched_videos / this.progress.total_videos) * 100
          )
        : 0
    },
    hasBeenRated() {
      return this.course.ratings && this.course.ratings.length !== 0
    },
    currentUserFeedback() {
      if (this.hasBeenRated && this.isAuthenticated) {
        const currentRating = this.course.ratings.find(
          rating => rating.author_id === this.userId
        )
        return !!(currentRating && currentRating.rating)
      }
      return false
    },
    isUser() {
      return !!(this.isAuthenticated && this.userId)
    },
    totalVideosWatched() {
      if (this.progress && this.progress.watched_videos) {
        return this.progress.watched_videos
      }
      return 0
    },
    nextVideo() {
      if (this.totalVideosWatched !== 0 && this.course.videos.length) {
        const videoIndices = []
        this.progress.videos.map(video => {
          this.course.videos.map(courseVideo => {
            if (video.id === courseVideo.id) {
              videoIndices.push(this.course.videos.indexOf(courseVideo))
            }
          })
        })
        const next = Math.max(...videoIndices) + 1
        return this.course.videos[next]
      }
      return this.course.videos[0]
    },
    gradient() {
      return this.$getGradient()
    },
  },
  watch: {
    course(course) {
      const videoCount = course.videos.length
      if (videoCount === 0) {
        window.location.assign(`https://streaming.ine.com/e/${course.slug}`)
      }
    },
  },
  methods: {
    resumeCourse() {
      this.$store.dispatch('content/setPlayingVideoId', this.nextVideo.id)
    },
    cleanUpContent() {
      this.$store.dispatch('content/setPlayingVideoId', '')
      this.$store.dispatch('content/setPlayingVideoTime', 0)
      this.$store.dispatch('content/setIsWatchingFullscreen', false)
      this.$store.dispatch('content/setCurrentQuizId', '')
    },
  },
  async created() {
    this.$store.dispatch('app/setIsPageLoading', true)

    if (this.$route.params.id === '6e2ef7cd-05da-4540-a95b-4a003889f41c') {
      this.$router.push({
        name: 'course',
        params: {
          slug: 'ine-intro-to-ccie-security-v6',
          id: '1d43c7ca-da42-4f46-8273-ad90d35fb94b',
        },
      })
      return
    }

    await this.$store.dispatch(
      'courses/getCourseByIdWithTopicLessons',
      this.$route.params.id
    )

    if (this.isAuthenticated && this.userId) {
      const courseIdAndUserId = {
        courseId: this.$route.params.id,
        userId: this.userId,
      }
      await this.$store.dispatch(
        'user/getUserCourseProgress',
        courseIdAndUserId
      )
      await this.$store.dispatch('user/getUserActiveCourses', this.userId)
    }

    this.$store.dispatch('app/setIsPageLoading', false)
  },
  destroyed() {
    this.cleanUpContent()
  },
}
</script>

<style lang="scss">
.course {
  background-color: var(--dark-gray);
  padding: 0 0 10% 0;
  margin: 0 auto;
  &__background {
    margin: 0 auto;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    padding: 150px 0 0 0;
  }
  &__overview-wrapper {
    max-width: 1900px;
    padding: 0 25px 100px 25px;
    margin: 0 auto;
    @include tablet {
      padding: 0 100px 75px 100px;
    }
  }
  &__details-wrapper {
    margin: 0 auto;
    padding: 0;
    max-width: 1900px;
    @include tablet {
      padding: 0 100px;
    }
  }
  &__content-wrapper {
    background-color: var(--dark-gray);
    max-width: 1900px;
    padding: 0;
    margin: 0 auto;
    padding: 100px 15px 0 15px;
    @include tablet {
      padding: 150px 100px 0 100px;
    }
  }
  &__progress-wrapper {
    width: 95%;
    margin: 0 auto;
  }
}
</style>
